import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { DealerDetailNav } from "./dealerAdminShared";
import {
  ANALYTICS_DATE_RANGES,
} from "../../../constants/buyerAnalyticsConstants";
import { TRIAL_READINESS_LABELS, formatPercent } from "../../../constants/automapDealerPerformanceConstants";
import { fetchAdminDealerFeatured } from "../../../services/automapFeaturedPlacementApi";
import { fetchAdminDealerPerformance } from "../../../services/automapDealerPerformanceApi";
import {
  AnalyticsRangeSelect,
  AnalyticsSummaryGrid,
  AnalyticsBreakdownTable,
} from "../analytics/AdminAnalyticsShared";

export default function AdminDealerPerformancePage() {
  const { dealerId } = useParams();
  const [range, setRange] = useState("last_30_days");
  const [payload, setPayload] = useState(null);
  const [featuredPayload, setFeaturedPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchAdminDealerPerformance(dealerId, range)
      .then(setPayload)
      .catch((err) => setError(err.message || "Error al cargar rendimiento."))
      .finally(() => setLoading(false));
    fetchAdminDealerFeatured(dealerId)
      .then(setFeaturedPayload)
      .catch(() => setFeaturedPayload(null));
  }, [dealerId, range]);

  const s = payload?.summary;
  const insights = payload?.adminInsights;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Concesionario"
        title={payload?.dealer?.businessName || "Rendimiento del concesionario"}
        description="Vista detallada para coaching y conversión de trial."
      />
      <AdminAutomotiveNav active="dealers" />
      <DealerDetailNav dealerId={dealerId} active="performance" />
      <main className="admin-console__main wrap">
        <div className="dealer-inventory-toolbar">
          <AnalyticsRangeSelect range={range} onChange={setRange} options={ANALYTICS_DATE_RANGES} />
          <Link className="dealer-portal-btn" to={`/admin/dealers/${dealerId}`}>
            Volver al concesionario
          </Link>
        </div>
        {loading ? <p className="dealer-state-box">Cargando…</p> : null}
        {error ? <p className="dealer-state-box">{error}</p> : null}
        {!loading && !error && s ? (
          <>
            <AnalyticsSummaryGrid
              items={[
                { label: "Vistas", value: s.totalListingViews },
                { label: "WhatsApp", value: s.totalWhatsappClicks },
                { label: "Leads", value: s.totalLeads },
                { label: "Calificados", value: s.qualifiedLeads },
                { label: "Malos", value: s.badLeads },
                { label: "Duplicados", value: s.duplicateLeads },
                { label: "Frescura inventario", value: formatPercent(s.inventoryFreshnessPercent) },
                { label: "Calidad promedio", value: s.averageListingQualityScore ?? "—" },
              ]}
            />
            <section className="dealer-state-box">
              <h3>Insights admin</h3>
              <ul className="dashboard-checklist">
                <li>Tasa malos leads: {formatPercent(insights?.badLeadRate)}</li>
                <li>Tasa duplicados: {formatPercent(insights?.duplicateLeadRate)}</li>
                <li>Problemas de respuesta: {insights?.responseProblems ? "Sí" : "No"}</li>
                <li>Impacto inventario desactualizado: {insights?.staleInventoryImpact ? "Sí" : "No"}</li>
                <li>Publicaciones alto riesgo: {insights?.highRiskListingCount ?? 0}</li>
                <li>
                  Preparación trial: {TRIAL_READINESS_LABELS[insights?.trialConversionReadiness] || "—"}
                </li>
              </ul>
              <p className="muted">{payload.trial?.adminMessage}</p>
            </section>
            <div className="admin-analytics-grid">
              <AnalyticsBreakdownTable title="Leads por fuente" rows={payload.leads?.bySource} keyLabel="Fuente" />
              <AnalyticsBreakdownTable title="Leads por estado" rows={payload.leads?.byStatus} keyLabel="Estado" />
            </div>
            <section className="dealer-state-box">
              <h3>Top publicaciones</h3>
              <table className="dealer-inventory-table">
                <thead>
                  <tr>
                    <th>Vehículo</th>
                    <th>Vistas</th>
                    <th>WhatsApp</th>
                    <th>Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {(payload.listings || []).slice(0, 10).map((row) => (
                    <tr key={row.listingId}>
                      <td>{row.vehicle}</td>
                      <td>{row.listingViews}</td>
                      <td>{row.whatsappClicks}</td>
                      <td>{row.leads}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            {featuredPayload ? (
              <section className="dealer-state-box">
                <h3>Placements destacados</h3>
                <AnalyticsSummaryGrid
                  items={[
                    { label: "Activos", value: featuredPayload.activeCount },
                    { label: "Impresiones", value: featuredPayload.totals?.impressions },
                    { label: "Clics", value: featuredPayload.totals?.clicks },
                    { label: "Leads", value: featuredPayload.totals?.leads },
                  ]}
                />
                <Link to="/admin/placements/performance">Ver performance completa →</Link>
              </section>
            ) : null}
            {(payload.recommendedActions || []).length ? (
              <section className="dealer-state-box">
                <h3>Acciones sugeridas para dealer success</h3>
                <ul className="dashboard-checklist">
                  {payload.recommendedActions.map((action) => (
                    <li key={action.id}>{action.message}</li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}
